import { useNavigate } from "react-router-dom";
import { X, Hammer, PencilLine, Camera, Film, ArrowRight, Users, Sparkles, Gift } from "lucide-react";

const STEPS = [
  {
    Icon: PencilLine,
    title: "Bring Your Knowhow",
    body: "Bring your passion, your specialty, or your unique interests to the community. Share what you know, written the way YOU do it. Nothing auto-generated—compose each step yourself, add as much detail as you like, and use your own voice, experience, and perspective to create something truly yours.",
  },
  {
    Icon: Camera,
    title: "15 steps — a photo on each",
    body: "You've got 15 steps to show the world exactly how you do what you do — snap a photo for every one. With a phone in every hand, you're already a creator. Real photos keep it honest and keep it yours, so show the community what makes your video unique, informative, and unmistakably you.",
  },
  {
    Icon: Film,
    title: "We Create a Video For You… and More!",
    body: "Of course you receive your video and your printable PDF manual — but that's just the start. You also get a ready-to-present Keynote/PowerPoint slide deck, all 15 step images, your Amazon Affiliate links sheet, printable handouts and answer keys, and more. It's a whole library of digital assets from one project — a lot of value for a little.",
  },
];

export default function DIYExplainerModal({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto"
      style={{ background: "rgba(23,20,38,.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="diy-explainer-modal"
    >
      <div className="flex min-h-full items-start md:items-center justify-center p-4">
      <div
        className="sms-tweed bg-[#0f0f16] rounded-[26px] w-full max-w-5xl my-6 relative border-2 border-[#5d8a52] text-white"
        style={{ boxShadow: "0 30px 80px -24px rgba(0,0,0,.7)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="diy-explainer-close"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-white/60 hover:bg-white/10 transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-5 md:p-7">
          {/* Top: hero + reward side-by-side on desktop to save vertical height */}
          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            <div className="flex flex-col">
              <div className="flex items-center gap-2 text-[#f0c975] font-bold text-sm">
                <Hammer size={16} /> CREATOR MODE
              </div>
              <h2 className="text-xl md:text-2xl font-extrabold mt-1.5 leading-tight text-white">
                You build it. We turn it into a video.
              </h2>
              <p className="text-white/70 mt-3 text-base md:text-[17px] leading-relaxed">
                This is your stage. Share your <span className="font-semibold text-white">skill</span>, your
                <span className="font-semibold text-white"> passion</span>, your
                <span className="font-semibold text-white"> knowhow</span> with the world — step by step, the way
                <span className="italic"> you'd</span> do it. No AI writing it for you. Just your words, your pics, your way.
                <span className="font-semibold text-white"> Make it your own.</span>
              </p>
            </div>

            {/* The reward for being original */}
            <div className="rounded-2xl border-2 border-[#5d8a52] bg-[#0e1a0b] p-4 flex flex-col" data-testid="diy-explainer-reward">
              <div className="flex items-center gap-2 font-extrabold text-white">
                <Gift size={17} className="text-[#4ade80]" /> Rewarded for being original
              </div>
              <p className="text-[13px] text-white/70 mt-1.5 leading-relaxed">
                Because the words and photos are all yours, <span className="font-bold text-[#4ade80]">building your manual is free</span> —
                no cost to write it or make images. You only spend when you decide to turn it into a video.
              </p>
              <p className="text-[13px] text-white/70 mt-2 leading-relaxed border-t border-white/10 pt-2">
                <span className="font-bold text-white">Tip: Prepare.</span> Have your pictures and text ready, then just
                copy/paste into the fields. <span className="font-semibold text-[#4ade80]">Happy Stepping!</span>
              </p>
            </div>
          </div>

          {/* 3 steps — horizontal */}
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            {STEPS.map(({ title, body }, i) => (
              <div key={i} className="rounded-2xl bg-black border-2 border-[#5d8a52] p-3.5">
                <div className="font-extrabold text-[13px] text-white leading-tight">{i + 1}. {title}</div>
                <div className="text-xs text-white/90 mt-1.5 leading-snug">{body}</div>
              </div>
            ))}
          </div>

          <p className="text-xs text-white/50 mt-3 leading-relaxed flex items-start gap-1.5">
            <Sparkles size={13} className="text-[#a9d79b] mt-0.5 shrink-0" />
            Keep it family-friendly and lawful. A clear photo on every step is required — it's what makes your manual come alive.
          </p>

          {/* CTAs — anchored at the bottom */}
          <div className="grid sm:grid-cols-2 gap-3 mt-4 items-stretch">
            <button
              data-testid="diy-explainer-start-btn"
              onClick={onClose}
              className="flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold py-3 px-2 rounded-2xl transition-colors"
            >
              <ArrowRight size={19} />
              <span className="text-sm">CREATE VIDEO</span>
            </button>
            <button
              data-testid="diy-explainer-community-btn"
              onClick={() => { onClose(); navigate("/"); }}
              className="flex items-center justify-center gap-2 bg-[#1e3aaa] hover:bg-[#182e85] text-white font-bold py-3 px-2 rounded-2xl transition-colors border-2 border-[#1e3aaa]"
            >
              <Users size={19} />
              <span className="text-sm">See the community</span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
